import { I18nService } from "../abstractions/i18n.service";
import { PlatformUtilsService } from "../abstractions/platformUtils.service";
import { ValidationService as ValidationServiceAbstraction } from "../abstractions/validation.service";
import { ErrorResponse } from "../models/response/error.response";

// Raw substrings surfaced by the OS/browser networking layer when the device has no
// connectivity (e.g. DNS resolution failures). These are technical and should never be
// shown to users directly - map them to a friendly, actionable message instead.
// Only include markers that unambiguously indicate the client has no connectivity/DNS
// resolution (not markers like ERR_CONNECTION_REFUSED/TIMED_OUT, which just as often mean the
// server itself is down - mislabeling those as a client network problem sends users down the
// wrong troubleshooting path).
const NETWORK_ERROR_MARKERS = [
  "network request failed",
  "unable to resolve host",
  "no address associated with hostname",
  "internet connection appears to be offline",
  "not connected to the internet",
  "network connection was lost",
  "err_internet_disconnected",
  "err_name_not_resolved",
  "err_network_changed",
  // Firefox
  "networkerror when attempting to fetch resource",
];

// Markers matched against the whole message only - as substrings they would also match
// unrelated errors (e.g. Safari's "Load failed" vs. "Download failed"/"Upload failed", or
// Chrome's "Failed to fetch" vs. app errors like "Failed to fetch coupons: HTTP 500").
const EXACT_NETWORK_ERROR_MESSAGES = [
  // Chrome
  "failed to fetch",
  // Safari
  "load failed",
];

function isNetworkErrorMessage(message: unknown): boolean {
  if (typeof message !== "string") {
    return false;
  }
  const lowerMessage = message.trim().toLowerCase();
  return (
    EXACT_NETWORK_ERROR_MESSAGES.includes(lowerMessage) ||
    NETWORK_ERROR_MARKERS.some((marker) => lowerMessage.includes(marker))
  );
}

export class ValidationService implements ValidationServiceAbstraction {
  constructor(
    private i18nService: I18nService,
    private platformUtilsService: PlatformUtilsService,
  ) {}

  showError(data: any): string[] {
    const defaultErrorMessage = this.i18nService.t("unexpectedError");
    const networkErrorMessage = this.i18nService.t("networkErrorOccurred");
    let errors: string[] = [];

    // The fetch wrappers (via toNetworkError) mark a genuine connectivity failure (no HTTP
    // response received at all - offline, DNS failure, etc.) with statusCode 0. That's a reliable, OS/locale-independent
    // signal, so trust it over pattern-matching message text.
    if (data instanceof ErrorResponse && data.statusCode === 0) {
      errors.push(networkErrorMessage);
    } else if (data != null && typeof data === "string") {
      // Fallback for callers that don't route through a fetch wrapper and just throw/reject a raw
      // string - best-effort substring match against known OS network-failure phrasing.
      errors.push(isNetworkErrorMessage(data) ? networkErrorMessage : data);
    } else if (data == null || typeof data !== "object") {
      errors.push(defaultErrorMessage);
    } else if (data.validationErrors != null) {
      errors = errors.concat((data as ErrorResponse).getAllMessages());
    } else {
      const message = data.message ? data.message : defaultErrorMessage;
      // Only fall back to message matching when there's no HTTP status to go on - an
      // ErrorResponse here came from a real server response (status 0 is handled above), and
      // its message may legitimately read e.g. "Failed to fetch transactions".
      errors.push(
        !(data instanceof ErrorResponse) && isNetworkErrorMessage(message)
          ? networkErrorMessage
          : message,
      );
    }

    if (errors.length === 1) {
      this.platformUtilsService.showToast("error", this.i18nService.t("errorOccurred"), errors[0]);
    } else if (errors.length > 1) {
      this.platformUtilsService.showToast("error", this.i18nService.t("errorOccurred"), errors, {
        timeout: 5000 * errors.length,
      });
    }

    return errors;
  }
}
